import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryService from "../api/CategoryService";
import DocumentTypeService from "../api/DocumentTypeService";
import CustomSnackbar from "../components/common/CustomSnackbar";
import { MDBContainer } from "mdb-react-ui-kit";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const AdminDocumentTypesPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [allDocTypes, setAllDocTypes] = useState([]);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      loadData();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, dtRes] = await Promise.all([
        CategoryService.getAll(),
        DocumentTypeService.getAll()
      ]);
      const fixedCatRes = catRes.data.map(c => ({
        ...c,
        documentTypeMap: c.documentTypeMap || {}
      }));
      setCategories(fixedCatRes);
      setAllDocTypes(dtRes.data);
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка: " + e.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updated) => {
    if (!updated.name?.trim()) {
      setShowValidation(true);
      setSnackbar({ open: true, message: "Поле 'Назва' є обовʼязковим.", severity: "error" });
      return;
    }
    try {
      if (updated.id) await CategoryService.update(updated);
      else await CategoryService.create(updated);
      setSnackbar({ open: true, message: "Збережено!", severity: "success" });
      setEditDialogOpen(false);
      loadData();
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка збереження: " + e.message, severity: "error" });
    }
  };

  const promptDelete = (cat) => {
    setCategoryToDelete(cat);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await CategoryService.deleteById(categoryToDelete.id);
      setSnackbar({ open: true, message: "Видалено!", severity: "success" });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
    } catch (e) {
      setSnackbar({ open: true, message: "Помилка видалення: " + e.message, severity: "error" });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "name", headerName: "Назва", flex: 1 },
    { field: "description", headerName: "Опис", flex: 2 },
    {
      field: "documentTypeMap",
      headerName: "Типи документів",
      flex: 2,
      valueGetter: (value) => JSON.stringify(value)
    },
    { field: "orderNumber", headerName: "Порядок", type: "number", width: 120 },
    {
      field: "actions", type: "actions", headerName: "Дії", width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon/>} label="Редагувати"
          onClick={() => {
            setCategoryToEdit({ ...params.row, documentTypeMap: params.row.documentTypeMap || {} });
            setEditDialogOpen(true);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon/>} label="Видалити"
          onClick={() => promptDelete(params.row)}
        />
      ]
    }
  ];

  return (
    <Box p={2} marginLeft={5} marginRight={5}>
      <MDBContainer className="d-flex justify-content-center align-items-center text-center">
        <Typography variant="h5" mb={2} marginRight={20}>Категорії</Typography>
        <Button
          startIcon={<AddIcon/>} variant="contained"
          sx={{ mb: 2 }}
          onClick={() => {
            setCategoryToEdit({ name: "", description: "", orderNumber: categories.length + 1, documentTypeMap: {} });
            setEditDialogOpen(true);
            setShowValidation(false);
          }}
        >
          Додати категорію
        </Button>
      </MDBContainer>

      {loading ? <CircularProgress/> : (
        <DataGrid
          rows={categories}
          columns={columns}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10]}
          getRowId={(row) => row.id}
          rowSelection={false}
          showToolbar={true}
        />
      )}

      {/* ✏️ Діалог створення / редагування */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{categoryToEdit?.id ? "Редагування категорії" : "Створення категорії"}</DialogTitle>
        <DialogContent>
          <TextField
            required fullWidth margin="dense" label="Назва"
            value={categoryToEdit?.name || ""}
            onChange={(e) => {
              setCategoryToEdit((c) => ({ ...c, name: e.target.value }));
              setShowValidation(false);
            }}
            error={showValidation && !categoryToEdit?.name?.trim()}
            helperText={showValidation && !categoryToEdit?.name?.trim() ? "Назва обовʼязкова" : ""}
          />
          <TextField
            fullWidth margin="dense" label="Опис" multiline
            value={categoryToEdit?.description || ""}
            onChange={(e) => setCategoryToEdit((c) => ({ ...c, description: e.target.value }))}
          />
          <TextField
            fullWidth margin="dense" label="Order Number" type="number"
            value={categoryToEdit?.orderNumber || ""}
            onChange={(e) => setCategoryToEdit((c) => ({ ...c, orderNumber: parseInt(e.target.value) || 0 }))}
          />

          {categoryToEdit && (
            <>
              <Typography variant="subtitle1" mt={2} mb={1}>Призначити типи документів</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[...allDocTypes]
                  .sort((a, b) => {
                    const orderA = categoryToEdit.documentTypeMap?.[a.id] ?? Infinity;
                    const orderB = categoryToEdit.documentTypeMap?.[b.id] ?? Infinity;
                    return orderA - orderB;
                  })
                  .map((dt) => {
                    const isChecked = dt.id in (categoryToEdit.documentTypeMap || {});
                    return (
                      <Box
                        key={dt.id}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                        py={1}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                const map = { ...categoryToEdit.documentTypeMap };
                                if (e.target.checked) {
                                  const currentOrders = Object.values(map);
                                  const maxOrder = currentOrders.length > 0 ? Math.max(...currentOrders) : 0;
                                  map[dt.id] = maxOrder + 1;
                                } else {
                                  delete map[dt.id];
                                }
                                setCategoryToEdit((c) => ({ ...c, documentTypeMap: map }));
                              }}
                            />
                          }
                          label={dt.name}
                        />
                        {isChecked && (
                          <TextField
                            label="Порядок"
                            type="number"
                            size="small"
                            value={categoryToEdit.documentTypeMap[dt.id]}
                            onChange={(e) => {
                              const map = { ...categoryToEdit.documentTypeMap };
                              map[dt.id] = parseInt(e.target.value) || 1;
                              setCategoryToEdit((c) => ({ ...c, documentTypeMap: map }));
                            }}
                            sx={{ width: 100 }}
                          />
                        )}
                      </Box>
                    );
                  })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="success" onClick={() => handleSave(categoryToEdit)}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🗑️ Підтвердження видалення */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Підтвердження видалення</DialogTitle>
        <DialogContent>
          <Typography>Впевнені, що хочете видалити <strong>{categoryToDelete?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Видалити</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AdminDocumentTypesPage;
